import classnames from 'classnames';
import Link from 'next/link';
import createRandomKey from 'helpers/createRandomKey';
import { useCartContext } from 'context/CartContext';
import { toast } from 'react-toastify';
import { CardTypes } from '.prisma/client';
import { useMenuContext } from 'context/MenuContext';
import getImageUrl from 'helpers/getImageUrl';
import { getTranslation } from 'dictionary';
import { useUserContext } from 'context/UserContext';
import { ProductWithRelations } from 'Types';
import { getFormatedPrice } from 'helpers';
import { getHighAndLowestPrice } from 'helpers/productHelpers';
import { PlusIcon } from '@heroicons/react/outline';

interface Props {
  item: ProductWithRelations;
  cardType: CardTypes | 'carousel';
}

const MenuCard: React.FC<Props> = ({ item, cardType }) => {
  const { language } = useUserContext();
  const { slug: storeSlug } = useMenuContext();

  const cardclassname = classnames({
    'overflow-hidden scroll-my-96 cursor-pointer flex flex-col h-fit': true,
    'w-full max-w-normal-card rounded-xl shadow': cardType === 'normal',
    'w-full max-w-small-card rounded-xl shadow justify-self-center':
      cardType === 'small',
    'opacity-50 cursor-not-allowed rounded-xl shadow':
      item.status === 'not_available',
    'w-full sm:max-w-normal-card min-h-0': cardType === 'carousel',
    'min-h-full': item.images?.[0],
  });

  const cardTitleclassname = classnames({
    'cardTitle font-medium text-lg ': true,
    '': cardType === 'normal',
    'h-14': cardType === 'small',
  });
  const tag = classnames(
    'px-3 py-1.5 rounded-4 absolute top-3 left-3 z-10 uppercase shadow-md text-xs tracking-wider bg-black/50 text-white'
  );

  const { dispatch } = useCartContext();
  const notify = () =>
    toast.success(getTranslation('menu', 'ADDED_TO_CART', language));
  const { currency_symbol, features } = useMenuContext();

  if (item.status === 'hidden') return null;

  const price = getHighAndLowestPrice(item);
  return (
    <div className={cardclassname} id={item.slug && item.slug}>
      {item.images?.[0] && (
        <Link className="relative w-full" href={`/${storeSlug}/${item.slug}`}>
          <div className="w-full  aspect-square	overflow-hidden scroll-mt-24 relative">
            <img
              src={getImageUrl(item.images[0].url, 600)}
              alt={item.title}
              className="object-cover w-full h-full"
              height={342}
              width={342}
            />
            {item.price_promotional ? (
              <span className={tag}>
                {getTranslation('menu', 'PROMOTION', language)}
              </span>
            ) : null}
          </div>
        </Link>
      )}
      <div
        className={classnames(
          'py-4 px-4 text-left flex flex-col justify-between bg-white',
          {
            'grow ': item.images?.[0],
          }
        )}
      >
        <Link
          href={`/${storeSlug}/${item.slug}`}
          className={cardTitleclassname}
        >
          {item.title}
        </Link>
        {item.description && (
          <Link
            href={`/${storeSlug}/${item.slug}`}
            className="cardDescription font-light text-base my-2"
          >
            {item.description}
          </Link>
        )}

        <div className="flex justify-between items-end">
          <Link
            href={`/${storeSlug}/${item.slug}`}
            className="flex flex-wrap gap-x-2 items-center"
          >
            <span
              className={classnames('text-base', {
                'font-normal': ! item.price_promotional,
                'line-through text-gray-500 font-light':
                  item.price_promotional,
              })}
            >
              {price.uniquePrice ? (
                getFormatedPrice(item.price, currency_symbol)
              ) : (
                <span>
                  {getFormatedPrice(price.lowestPrice || 0, currency_symbol)} -{' '}
                  {getFormatedPrice(price.highestPrice || 0, currency_symbol)}
                </span>
              )}
            </span>
            {item.price_promotional && (
              <span className="font-base text-base">
                {price.uniquePrice ? (
                  getFormatedPrice(item.price_promotional, currency_symbol)
                ) : (
                  <span>
                    {getFormatedPrice(
                      price.lowestPromotionalPrice || 0,
                      currency_symbol
                    )}{' '}
                    -{' '}
                    {getFormatedPrice(
                      price.highestPromotionalPrice || 0,
                      currency_symbol
                    )}
                  </span>
                )}
              </span>
            )}
          </Link>

          {/*TODO: Change when we add variants  */}
          {item.status === 'not_available' ||
          item.variants?.length ||
          features === 'CATALOGO' ? null : (
            <button
              aria-label="add to cart"
              className="ripple-bg-gray-50 bg-white text-black text-xs font-base p-1 hover:bg-white rounded-full border border-black"
              onClick={(e) => {
                e.stopPropagation();
                //@ts-ignore
                dispatch({
                  type: 'add_to_cart',
                  payload: {
                    itemId: createRandomKey(),
                    product: item,
                    count: 1,
                    extrasSelection: null,
                    variantSelection: {},
                  },
                });
                notify();
              }}
            >
              <PlusIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuCard;
